<script setup lang="ts">
    import { MEILISEARCH_MODEL } from '~/app/common/enums/MeilisearchIndexType';
    import type { WorksitesIndexSchema } from '~/app/common/schemas/WorksitesIndexSchema';
    import { useSearchDropdown } from '~/composables/useSearchDropdown';

    interface Fields {
        title: string;
        streetName: string;
        streetNumber: string;
        postalCode: string;
        city: string;
    }

    const props = defineProps<{
        searchTerm: string;
    }>();

    const emit = defineEmits<{
        submit: [data: { title: string; streetName?: string; streetNumber?: string; postalCode?: string; city?: string }];
        cancel: [];
        'select-existing': [uuid: string];
    }>();

    const {
        results: worksiteResults,
        showDropdown,
        search: searchWorksites,
        hideDropdown,
    } = useSearchDropdown<WorksitesIndexSchema>({ model: MEILISEARCH_MODEL.WORKSITES, debounceMs: 200 });

    const form = useForm<Fields>({
        title: props.searchTerm,
        streetName: '',
        streetNumber: '',
        postalCode: '',
        city: '',
    });

    const triggerSearch = () => {
        searchWorksites(form.fields.title);
    };

    const selectExisting = (worksite: WorksitesIndexSchema) => {
        showDropdown.value = false;
        emit('select-existing', worksite.uuid);
    };

    const submitForm = () => {
        emit('submit', {
            title: form.fields.title,
            streetName: form.fields.streetName || undefined,
            streetNumber: form.fields.streetNumber || undefined,
            postalCode: form.fields.postalCode || undefined,
            city: form.fields.city || undefined,
        });
    };
</script>

<template>
    <div class="rounded-[12px] border border-[#E7E7E7] bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,16,32,0.04)]">
        <!-- Header band -->
        <div
            class="flex items-center gap-[10px] px-[14px] py-3 border-b border-[#E7E7E7]"
            style="background: linear-gradient(180deg, #FEF8EA 0%, #FEFCF6 100%)"
        >
            <span class="w-6 h-6 rounded-full bg-branding-brand-yellow border border-[#E8B102] flex items-center justify-center flex-shrink-0 shadow-sm">
                <i class="fa-solid fa-helmet-safety text-[11px] text-black" />
            </span>
            <div class="flex-1 min-w-0">
                <p class="font-bricolage font-semibold text-[14px] text-gray-900 leading-[18px]">
                    {{ $t('ai.form.new_worksite') }}
                </p>
                <p class="font-inter text-[11px] text-gray-400 mt-[1px] truncate">
                    {{ $t('ai.form.from', { seed: searchTerm || '...' }) }}
                </p>
            </div>
        </div>

        <!-- Body -->
        <div class="px-[14px] py-[14px] flex flex-col gap-[12px]">
            <!-- Title with Meilisearch dropdown -->
            <div class="relative">
                <FormInput
                    :form="form"
                    input-name="title"
                    :placeholder="$t('ai.form.search_worksite')"
                    @input="triggerSearch"
                    @blur="hideDropdown"
                />
                <div
                    v-if="showDropdown && worksiteResults.length"
                    class="absolute top-full left-0 right-0 mt-[4px] bg-white border border-[#E7E7E7] rounded-md shadow-md z-10 max-h-[180px] overflow-y-auto"
                >
                    <button
                        v-for="worksite in worksiteResults"
                        :key="worksite.uuid"
                        type="button"
                        class="w-full text-left px-3 py-2 hover:bg-[#FEFCF6] border-b border-gray-50 last:border-b-0"
                        @mousedown.prevent="selectExisting(worksite)"
                    >
                        <span class="block font-inter text-[12px] text-gray-900 truncate">{{ worksite.title }}</span>
                        <span v-if="worksite.address_display" class="block font-inter text-[10px] text-gray-400">{{ worksite.address_display }}</span>
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-[3fr_1fr] gap-[10px]">
                <FormInput :form="form" input-name="streetName" />
                <FormInput :form="form" input-name="streetNumber" />
            </div>

            <div class="grid grid-cols-2 gap-[10px]">
                <FormInput :form="form" input-name="postalCode" />
                <FormInput :form="form" input-name="city" />
            </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-2 px-[14px] py-[10px] border-t border-[#E7E7E7] bg-branding-sea-salt">
            <button
                type="button"
                class="px-3 py-[7px] font-inter text-[13px] font-medium text-gray-700 bg-white border border-[#D3D2D2] rounded-md hover:bg-gray-50 shadow-sm cursor-pointer"
                @click="emit('cancel')"
            >
                {{ $t('actions.cancel') }}
            </button>
            <button
                type="button"
                class="px-3 py-[7px] font-inter text-[13px] font-medium rounded-md shadow-sm transition-colors"
                :class="!form.fields.title
                    ? 'opacity-40 cursor-not-allowed bg-branding-brand-yellow border border-[#E8B102] text-black'
                    : 'bg-branding-brand-yellow border border-[#E8B102] text-black hover:bg-branding-mustard cursor-pointer'"
                :disabled="!form.fields.title"
                @click="submitForm"
            >
                {{ $t('actions.create_worksite') }}
            </button>
        </div>
    </div>
</template>
